import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import type {
  IAccountingCode,
  IAccountingCodeResponse,
} from 'types/AccountingCode/AccountingCodes.types';
import type {
  OnboardingAccountingCodesQueryParams,
  OnboardingPoAutogenRuleResponse,
  OnboardingEmployeeListParams,
  OnboardingEmployeeListResponse,
  OnboardingEmployeeRateSheet,
  OnboardingEmployeeResourceCode,
  ResourceTemplatesResponse,
  ResourceTypeDropdownItem,
  XeroTrackingCategoriesResponse,
  TenantOnboardingResponse,
  TenantOnboardingSkippedResponse,
} from '../types/onboardingApi';
import {
  fetchOnboardingAccountingCodes,
  fetchOnboardingAccountingCodesDropdown,
  fetchOnboardingPoAutogenRule,
  fetchOnboardingEmployeeList,
  fetchOnboardingEmployeeRateSheet,
  fetchOnboardingEmployeeResourceCodes,
  fetchOnboardingResourceTemplates,
  fetchOnboardingResourceTypeDropdown,
  fetchOnboardingXeroTrackingCategories,
  fetchTenantOnboarding,
  fetchTenantOnboardingSkipped,
} from './services';
import { ONBOARDING_QUERY_KEYS } from './keys';

export const useOnboardingPoAutogenRuleQuery = (
  options?: Partial<
    UseQueryOptions<OnboardingPoAutogenRuleResponse | undefined>
  >,
): UseQueryResult<OnboardingPoAutogenRuleResponse | undefined> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.PO_AUTOGEN_RULE],
    queryFn: fetchOnboardingPoAutogenRule,
    ...options,
  });

export const useOnboardingAccountingCodesQuery = ({
  queryParams,
  options,
}: {
  queryParams: OnboardingAccountingCodesQueryParams;
  options?: Partial<UseQueryOptions<IAccountingCodeResponse>>;
}): UseQueryResult<IAccountingCodeResponse> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.ACCOUNTING_CODES_LIST, queryParams],
    queryFn: () => fetchOnboardingAccountingCodes(queryParams),
    ...options,
  });

export const useOnboardingAccountingCodesDropdownQuery = (
  options?: Partial<UseQueryOptions<IAccountingCode[]>>,
): UseQueryResult<IAccountingCode[]> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.ACCOUNTING_CODES_DROPDOWN],
    queryFn: fetchOnboardingAccountingCodesDropdown,
    ...options,
  });

export const useOnboardingResourceTemplatesQuery = (
  options?: Partial<UseQueryOptions<ResourceTemplatesResponse>>,
): UseQueryResult<ResourceTemplatesResponse> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.RESOURCE_TEMPLATES],
    queryFn: fetchOnboardingResourceTemplates,
    ...options,
  });

export const useOnboardingResourceTypeDropdownQuery = (
  options?: Partial<UseQueryOptions<ResourceTypeDropdownItem[]>>,
): UseQueryResult<ResourceTypeDropdownItem[]> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.RESOURCE_TYPE_DROPDOWN],
    queryFn: fetchOnboardingResourceTypeDropdown,
    ...options,
  });

export const useOnboardingXeroTrackingCategoriesQuery = ({
  tenantId,
  options,
}: {
  tenantId: string | undefined;
  options?: Partial<UseQueryOptions<XeroTrackingCategoriesResponse>>;
}): UseQueryResult<XeroTrackingCategoriesResponse> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.TRACKING_CATEGORIES, tenantId],
    queryFn: () => fetchOnboardingXeroTrackingCategories(tenantId as string),
    enabled: Boolean(tenantId),
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });

export const useOnboardingEmployeeResourceCodesQuery = (
  options?: Partial<UseQueryOptions<OnboardingEmployeeResourceCode[]>>,
): UseQueryResult<OnboardingEmployeeResourceCode[]> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.EMPLOYEE_RESOURCE_CODES],
    queryFn: fetchOnboardingEmployeeResourceCodes,
    ...options,
  });

export const useOnboardingEmployeeListQuery = ({
  queryParams,
  options,
}: {
  queryParams: OnboardingEmployeeListParams;
  options?: Partial<UseQueryOptions<OnboardingEmployeeListResponse>>;
}): UseQueryResult<OnboardingEmployeeListResponse> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.EMPLOYEE_LIST, queryParams],
    queryFn: () => fetchOnboardingEmployeeList(queryParams),
    ...options,
  });

export const useTenantOnboardingQuery = (
  options?: Partial<UseQueryOptions<TenantOnboardingResponse>>,
): UseQueryResult<TenantOnboardingResponse> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.TENANT_ONBOARDING],
    queryFn: fetchTenantOnboarding,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });

export const useTenantOnboardingSkippedQuery = ({
  tenantId,
  options,
}: {
  tenantId: string | undefined;
  options?: Partial<UseQueryOptions<TenantOnboardingSkippedResponse>>;
}): UseQueryResult<TenantOnboardingSkippedResponse> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.TENANT_ONBOARDING_SKIPPED, tenantId],
    queryFn: fetchTenantOnboardingSkipped,
    enabled: Boolean(tenantId),
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });

export const useOnboardingEmployeeRateSheetQuery = (
  options?: Partial<UseQueryOptions<OnboardingEmployeeRateSheet>>,
): UseQueryResult<OnboardingEmployeeRateSheet> =>
  useQuery({
    queryKey: [ONBOARDING_QUERY_KEYS.EMPLOYEE_RATE_SHEET],
    queryFn: fetchOnboardingEmployeeRateSheet,
    refetchOnWindowFocus: false,
    ...options,
  });
